import { canWriteSchoolScopedDataCache } from './schoolDataCacheVersion';
import {
  emptyGroupMemberPoints,
  emptyItemCardSet,
  emptyPointItemNoteDoc,
  emptyPointLevelSettings,
  emptyClassPointSettings,
  emptyPointTypeSet,
  type AssignmentPointKind,
  type AssignmentPointStatus,
  type ClassPointRewardTier,
  type ClassPointSettings,
  type GroupMemberPoints,
  type ItemCard,
  type ItemCardSet,
  type ItemCardUserImage,
  type CoinRecordType,
  type MemberAssignmentPoint,
  type MemberCoinRecord,
  type ItemUsageRecord,
  type MemberItemInstance,
  type MemberNoteRecord,
  type MemberNoteType,
  type MemberPointRecord,
  type PointItemNoteDoc,
  type PointLevelSettings,
  type PointTypeItem,
  type PointTypeSet,
} from '../types/pointItemNote';

function storageKey(workspaceId: string, gradeId: string): string {
  // v2: 멤버 이력은 테이블 기준; 설정만 payload에 유지
  return `cp.pointItemNote.v2.${workspaceId}.${gradeId}`;
}

/**
 * 그룹 단위로 테이블 이력이 로드 완료된 groupId 목록.
 * v4: 과제 재병합 경로 포함 — 빈 assignmentPoints로 loaded 고정된 v3 캐시 무효화.
 */
function loadedGroupsKey(workspaceId: string, gradeId: string): string {
  return `cp.pointItemNote.v4.${workspaceId}.${gradeId}.loadedGroups`;
}

function normalizePointType(raw: unknown, category: 'positive' | 'negative'): PointTypeItem | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : '';
  if (!id) return null;
  const now = new Date().toISOString();
  return {
    id,
    points: Number(o.points) || 0,
    memo: typeof o.memo === 'string' ? o.memo : '',
    category,
    emoji: typeof o.emoji === 'string' && o.emoji.trim() ? o.emoji.trim() : undefined,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : now,
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : now,
  };
}

function normalizePointSet(raw: unknown): PointTypeSet {
  const base = emptyPointTypeSet();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  return {
    positiveTypes: Array.isArray(o.positiveTypes)
      ? o.positiveTypes
          .map((x) => normalizePointType(x, 'positive'))
          .filter((x): x is PointTypeItem => x != null)
      : [],
    negativeTypes: Array.isArray(o.negativeTypes)
      ? o.negativeTypes
          .map((x) => normalizePointType(x, 'negative'))
          .filter((x): x is PointTypeItem => x != null)
      : [],
    levelUpPoints: Number(o.levelUpPoints) || 50,
  };
}

function normalizeItemCard(raw: unknown, category: ItemCard['category']): ItemCard | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const itemCardId = typeof o.itemCardId === 'string' ? o.itemCardId : '';
  if (!itemCardId) return null;
  const now = new Date().toISOString();
  const costRaw = o.cost;
  const cost: number | 'Nocharge' =
    costRaw === 'Nocharge' ? 'Nocharge' : Number(costRaw) || 0;
  const type = o.type === 'active' ? 'active' : 'passive';
  return {
    itemCardId,
    cost,
    type,
    useCount: type === 'active' ? Number(o.useCount) || 1 : undefined,
    cardImage: typeof o.cardImage === 'string' ? o.cardImage : 'default',
    name: typeof o.name === 'string' ? o.name : '',
    memo: typeof o.memo === 'string' ? o.memo : '',
    category,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : now,
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : now,
  };
}

function normalizeItemCards(raw: unknown): ItemCardSet {
  const base = emptyItemCardSet();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  return {
    levelupItem: Array.isArray(o.levelupItem)
      ? o.levelupItem
          .map((x) => normalizeItemCard(x, 'levelup'))
          .filter((x): x is ItemCard => x != null)
      : [],
    assignmentItem: Array.isArray(o.assignmentItem)
      ? o.assignmentItem
          .map((x) => normalizeItemCard(x, 'assignment'))
          .filter((x): x is ItemCard => x != null)
      : [],
    storeItem: Array.isArray(o.storeItem)
      ? o.storeItem
          .map((x) => normalizeItemCard(x, 'store'))
          .filter((x): x is ItemCard => x != null)
      : [],
  };
}

function normalizeItemCardUserImage(raw: unknown): ItemCardUserImage | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : '';
  const path = typeof o.path === 'string' ? o.path : '';
  const url = typeof o.url === 'string' ? o.url : '';
  if (!id || !path || !url) return null;
  const slotRaw = Number(o.slotIndex);
  const slotIndex =
    Number.isFinite(slotRaw) && slotRaw >= 0 ? Math.trunc(slotRaw) : undefined;
  return {
    id,
    path,
    url,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : new Date().toISOString(),
    ...(slotIndex != null ? { slotIndex } : {}),
  };
}

function normalizeItemCardImages(raw: unknown): ItemCardUserImage[] {
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeItemCardUserImage)
    .filter((x): x is ItemCardUserImage => x != null);
}

function normalizeRecord(raw: unknown): MemberPointRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : '';
  const memberId = typeof o.memberId === 'string' ? o.memberId : '';
  if (!id || !memberId) return null;
  return {
    id,
    memberId,
    pointTypeId: typeof o.pointTypeId === 'string' ? o.pointTypeId : '',
    pointValue: Number(o.pointValue) || 0,
    pointMemo: typeof o.pointMemo === 'string' ? o.pointMemo : '',
    pointCategory: o.pointCategory === 'negative' ? 'negative' : 'positive',
    recordedDate: typeof o.recordedDate === 'string' ? o.recordedDate : '',
    timeTagLabel: typeof o.timeTagLabel === 'string' ? o.timeTagLabel : undefined,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : new Date().toISOString(),
  };
}

function normalizeNumberMap(raw: unknown): Record<string, number> {
  const out: Record<string, number> = {};
  if (!raw || typeof raw !== 'object') return out;
  Object.entries(raw as Record<string, unknown>).forEach(([k, v]) => {
    out[k] = Number(v) || 0;
  });
  return out;
}

function normalizeCoinType(raw: unknown): CoinRecordType {
  if (raw === 'Levelup' || raw === 'Store' || raw === 'Assignment') return raw;
  return 'General';
}

function normalizeCoinRecord(raw: unknown): MemberCoinRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : '';
  const memberId = typeof o.memberId === 'string' ? o.memberId : '';
  if (!id || !memberId) return null;
  return {
    id,
    memberId,
    coinValue: Number(o.coinValue) || 0,
    coinType: normalizeCoinType(o.coinType),
    memo: typeof o.memo === 'string' ? o.memo : undefined,
    recordedDate: typeof o.recordedDate === 'string' ? o.recordedDate : '',
    timeTagLabel: typeof o.timeTagLabel === 'string' ? o.timeTagLabel : undefined,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : new Date().toISOString(),
  };
}

function normalizeUsageHistory(raw: unknown): ItemUsageRecord[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const list = raw
    .map((entry): ItemUsageRecord | null => {
      if (!entry || typeof entry !== 'object') return null;
      const o = entry as Record<string, unknown>;
      const usedAt =
        typeof o.usedAt === 'string'
          ? o.usedAt
          : o.usedAt != null
            ? new Date(o.usedAt as string | number | Date).toISOString()
            : '';
      if (!usedAt || Number.isNaN(Date.parse(usedAt))) return null;
      const memo = typeof o.memo === 'string' ? o.memo : undefined;
      return memo ? { usedAt, memo } : { usedAt };
    })
    .filter((x): x is ItemUsageRecord => x != null);
  return list.length > 0 ? list : undefined;
}

function normalizeItemInstance(raw: unknown): MemberItemInstance | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : '';
  const memberId = typeof o.memberId === 'string' ? o.memberId : '';
  const itemCardId = typeof o.itemCardId === 'string' ? o.itemCardId : '';
  if (!id || !memberId || !itemCardId) return null;
  const cat = o.category;
  const category =
    cat === 'assignment' || cat === 'store' || cat === 'levelup' ? cat : 'levelup';
  const remaining =
    typeof o.remainingUseCount === 'number'
      ? o.remainingUseCount
      : o.remainingUseCount != null
        ? Number(o.remainingUseCount)
        : undefined;
  const usageHistory = normalizeUsageHistory(o.usageHistory);
  return {
    id,
    memberId,
    itemCardId,
    category,
    acquiredLevel: typeof o.acquiredLevel === 'number' ? o.acquiredLevel : undefined,
    remainingUseCount: Number.isFinite(remaining as number) ? (remaining as number) : undefined,
    usageHistory,
    isLocked: o.isLocked === true,
    acquiredAt: typeof o.acquiredAt === 'string' ? o.acquiredAt : new Date().toISOString(),
  };
}

function normalizeRewardedLevels(raw: unknown): Record<string, number[]> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, number[]> = {};
  Object.entries(raw as Record<string, unknown>).forEach(([memberId, levels]) => {
    if (!Array.isArray(levels)) return;
    out[memberId] = levels.map((n) => Number(n)).filter((n) => Number.isFinite(n) && n > 0);
  });
  return out;
}

function normalizeNoteType(raw: unknown): MemberNoteType {
  if (raw === '학습' || raw === '행동') return raw;
  return '일반';
}

function normalizeNoteRecord(raw: unknown): MemberNoteRecord | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : '';
  const memberId = typeof o.memberId === 'string' ? o.memberId : '';
  if (!id || !memberId) return null;
  return {
    id,
    memberId,
    text: typeof o.text === 'string' ? o.text : '',
    noteType: normalizeNoteType(o.noteType),
    recordedDate: typeof o.recordedDate === 'string' ? o.recordedDate : '',
    timeTagLabel: typeof o.timeTagLabel === 'string' ? o.timeTagLabel : undefined,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : new Date().toISOString(),
  };
}

function normalizeAssignmentKind(raw: unknown): AssignmentPointKind {
  if (raw === 'project' || raw === 'routine') return raw;
  return 'general';
}

function normalizeAssignmentStatus(raw: unknown): AssignmentPointStatus {
  if (
    raw === 'confirmed' ||
    raw === 'delayed' ||
    raw === 'exempted' ||
    raw === 'excluded' ||
    raw === 'submitted'
  ) {
    return raw;
  }
  return 'pending';
}

function normalizeAssignmentPoint(raw: unknown): MemberAssignmentPoint | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  const id = typeof o.id === 'string' ? o.id : '';
  const memberId = typeof o.memberId === 'string' ? o.memberId : '';
  if (!id || !memberId) return null;
  return {
    id,
    memberId,
    groupId: typeof o.groupId === 'string' ? o.groupId : '',
    assignmentItemId: typeof o.assignmentItemId === 'string' ? o.assignmentItemId : '',
    assignmentSubId: typeof o.assignmentSubId === 'string' ? o.assignmentSubId : undefined,
    assignmentKind: normalizeAssignmentKind(o.assignmentKind),
    assignmentTitle: typeof o.assignmentTitle === 'string' ? o.assignmentTitle : '',
    status: normalizeAssignmentStatus(o.status),
    earnedPoints: Number(o.earnedPoints) || 0,
    earnedCoins: Number(o.earnedCoins) || 0,
    earnedItemIds: Array.isArray(o.earnedItemIds)
      ? o.earnedItemIds.filter((x): x is string => typeof x === 'string')
      : [],
    recordedDate: typeof o.recordedDate === 'string' ? o.recordedDate : '',
    memo: typeof o.memo === 'string' ? o.memo : undefined,
    createdAt: typeof o.createdAt === 'string' ? o.createdAt : new Date().toISOString(),
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : new Date().toISOString(),
  };
}

function normalizeGroupPoints(raw: unknown): GroupMemberPoints {
  if (!raw || typeof raw !== 'object') return emptyGroupMemberPoints();
  const o = raw as Record<string, unknown>;
  return {
    totals: normalizeNumberMap(o.totals),
    records: Array.isArray(o.records)
      ? o.records.map(normalizeRecord).filter((r): r is MemberPointRecord => r != null)
      : [],
    coinTotals: normalizeNumberMap(o.coinTotals),
    coinRecords: Array.isArray(o.coinRecords)
      ? o.coinRecords.map(normalizeCoinRecord).filter((r): r is MemberCoinRecord => r != null)
      : [],
    notes: Array.isArray(o.notes)
      ? o.notes.map(normalizeNoteRecord).filter((r): r is MemberNoteRecord => r != null)
      : [],
    rewardedLevels: normalizeRewardedLevels(o.rewardedLevels),
    itemInstances: Array.isArray(o.itemInstances)
      ? o.itemInstances
          .map(normalizeItemInstance)
          .filter((r): r is MemberItemInstance => r != null)
      : [],
    assignmentPoints: Array.isArray(o.assignmentPoints)
      ? o.assignmentPoints
          .map(normalizeAssignmentPoint)
          .filter((r): r is MemberAssignmentPoint => r != null)
      : [],
    assignmentTotals: normalizeNumberMap(o.assignmentTotals),
  };
}

function normalizeLevelSettings(raw: unknown, fallbackLevelUp: number): PointLevelSettings {
  const base = emptyPointLevelSettings();
  base.levelUpPoints = Math.max(1, fallbackLevelUp || 50);
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  const levelUpPoints = Number(o.levelUpPoints);
  const levelCoins: Record<number, number> = {};
  if (o.levelCoins && typeof o.levelCoins === 'object') {
    Object.entries(o.levelCoins as Record<string, unknown>).forEach(([k, v]) => {
      const level = Number(k);
      if (!Number.isFinite(level) || level < 1) return;
      levelCoins[level] = Math.max(0, Number(v) || 0);
    });
  }
  const levelItemCards: Record<number, string[]> = {};
  if (o.levelItemCards && typeof o.levelItemCards === 'object') {
    Object.entries(o.levelItemCards as Record<string, unknown>).forEach(([k, v]) => {
      const level = Number(k);
      if (!Number.isFinite(level) || level < 1 || !Array.isArray(v)) return;
      levelItemCards[level] = v.filter((id): id is string => typeof id === 'string' && !!id);
    });
  }
  return {
    levelUpPoints: Number.isFinite(levelUpPoints) && levelUpPoints > 0 ? levelUpPoints : base.levelUpPoints,
    levelCoins,
    levelItemCards,
  };
}

function normalizeClassPointSettings(raw: unknown): ClassPointSettings {
  const base = emptyClassPointSettings();
  if (!raw || typeof raw !== 'object') return base;
  const o = raw as Record<string, unknown>;
  const tiersRaw = Array.isArray(o.rewardTiers) ? o.rewardTiers : [];
  const rewardTiers: ClassPointRewardTier[] = [];
  for (const t of tiersRaw) {
    if (!t || typeof t !== 'object') continue;
    const row = t as Record<string, unknown>;
    const id = typeof row.id === 'string' ? row.id : '';
    if (!id) continue;
    const now = new Date().toISOString();
    const cards = Array.isArray(row.cards)
      ? row.cards
          .map((c) => normalizeItemCard(c, 'classPoint'))
          .filter((c): c is ItemCard => c != null)
      : [];
    rewardTiers.push({
      id,
      thresholdPoints: Math.max(0, Number(row.thresholdPoints) || 0),
      cards,
      createdAt: typeof row.createdAt === 'string' ? row.createdAt : now,
      updatedAt: typeof row.updatedAt === 'string' ? row.updatedAt : now,
    });
  }
  rewardTiers.sort((a, b) => a.thresholdPoints - b.thresholdPoints || a.id.localeCompare(b.id));
  const grantedTierByGroup: Record<string, string[]> = {};
  if (o.grantedTierByGroup && typeof o.grantedTierByGroup === 'object') {
    Object.entries(o.grantedTierByGroup as Record<string, unknown>).forEach(([gid, ids]) => {
      if (!Array.isArray(ids)) return;
      grantedTierByGroup[gid] = ids.filter((x): x is string => typeof x === 'string');
    });
  }
  return {
    enabled: o.enabled === true,
    integrateAllGroups: o.integrateAllGroups === true,
    rewardTiers,
    grantedTierByGroup,
  };
}

export function normalizePointItemNoteDoc(
  raw: unknown,
  workspaceId: string,
  gradeId: string
): PointItemNoteDoc {
  const empty = emptyPointItemNoteDoc(workspaceId, gradeId);
  if (!raw || typeof raw !== 'object') return empty;
  const o = raw as Record<string, unknown>;
  const byGroup: Record<string, GroupMemberPoints> = {};
  if (o.memberPointsByGroup && typeof o.memberPointsByGroup === 'object') {
    Object.entries(o.memberPointsByGroup as Record<string, unknown>).forEach(([gid, v]) => {
      byGroup[gid] = normalizeGroupPoints(v);
    });
  }
  const pointSet = normalizePointSet(o.pointSet);
  const levelSettings = normalizeLevelSettings(
    o.levelSettings,
    pointSet.levelUpPoints || 50
  );
  // levelSettings.levelUpPoints를 pointSet과 동기화
  pointSet.levelUpPoints = levelSettings.levelUpPoints;

  // payload에 따로 저장된 rewardedLevels 병합
  if (o.memberRewardedLevelsByGroup && typeof o.memberRewardedLevelsByGroup === 'object') {
    Object.entries(o.memberRewardedLevelsByGroup as Record<string, unknown>).forEach(
      ([gid, levels]) => {
        const rewarded = normalizeRewardedLevels(levels);
        if (!byGroup[gid]) byGroup[gid] = emptyGroupMemberPoints();
        byGroup[gid] = {
          ...byGroup[gid],
          rewardedLevels: {
            ...(byGroup[gid].rewardedLevels ?? {}),
            ...rewarded,
          },
        };
      }
    );
  }

  return {
    workspaceId,
    gradeId,
    pointSet,
    itemCards: normalizeItemCards(o.itemCards),
    itemCardImages: normalizeItemCardImages(o.itemCardImages),
    levelSettings,
    classPointSettings: normalizeClassPointSettings(o.classPointSettings),
    memberPointsByGroup: byGroup,
    updatedAt: typeof o.updatedAt === 'string' ? o.updatedAt : empty.updatedAt,
  };
}

/** 캐시만 동기 읽기 (DB 접근 없음) */
export function loadPointItemNoteCache(
  workspaceId: string,
  gradeId: string
): PointItemNoteDoc | null {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(storageKey(workspaceId, gradeId));
    if (!raw) return null;
    return normalizePointItemNoteDoc(JSON.parse(raw), workspaceId, gradeId);
  } catch {
    return null;
  }
}

export function savePointItemNoteCache(
  doc: PointItemNoteDoc,
  options?: { force?: boolean }
): void {
  if (!doc.workspaceId || !doc.gradeId || typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(doc.workspaceId)) return;
  try {
    window.localStorage.setItem(storageKey(doc.workspaceId, doc.gradeId), JSON.stringify(doc));
  } catch {
    // ignore
  }
}

/** 테이블에서 로드 완료된 그룹 ID 목록 */
export function loadPointItemNoteLoadedGroupIds(
  workspaceId: string,
  gradeId: string
): string[] {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(loadedGroupsKey(workspaceId, gradeId));
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string' && !!x);
  } catch {
    return [];
  }
}

export function savePointItemNoteLoadedGroupIds(
  workspaceId: string,
  gradeId: string,
  groupIds: string[],
  options?: { force?: boolean }
): void {
  if (!workspaceId || !gradeId || typeof window === 'undefined') return;
  if (!options?.force && !canWriteSchoolScopedDataCache(workspaceId)) return;
  try {
    const uniq = Array.from(new Set(groupIds.filter(Boolean)));
    window.localStorage.setItem(
      loadedGroupsKey(workspaceId, gradeId),
      JSON.stringify(uniq)
    );
  } catch {
    // ignore
  }
}

export function markPointItemNoteGroupLoaded(
  workspaceId: string,
  gradeId: string,
  groupId: string,
  options?: { force?: boolean }
): void {
  if (!groupId) return;
  const prev = loadPointItemNoteLoadedGroupIds(workspaceId, gradeId);
  if (prev.includes(groupId)) return;
  savePointItemNoteLoadedGroupIds(
    workspaceId,
    gradeId,
    [...prev, groupId],
    options
  );
}

export function isPointItemNoteGroupLoaded(
  workspaceId: string,
  gradeId: string,
  groupId: string
): boolean {
  if (!groupId) return false;
  return loadPointItemNoteLoadedGroupIds(workspaceId, gradeId).includes(groupId);
}
